// ============================================================================
// VideoPhaseWorkflow (Iter31) — Phase 1 (dérush) + Phase 2 (montage) UI
//   • Affichage par rôle : dérusheur voit Phase 1, monteur voit Phase 2 (lecture
//     seule Phase 1), chef/founder voient tout et peuvent valider.
//   • Phase 2 — bloc consignes fusionné (titre + format + client + échéance +
//     inspi + drive). Bouton "J'ai lu les consignes" → débloque le montage.
//   • Workflow strict : ordre forcé côté backend (POST /tasks/:id/phase-step).
// ============================================================================
import { useMemo, useState, useEffect, Fragment } from 'react';
import { PublicationBlock as PublicationCopyBlock } from './PublicationCopyBlock';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { formatDay } from '../lib/dates';
import MessagesPopup from './MessagesPopup';
import {
  Check, CheckCircle, Loader2, Upload, AlertTriangle, Calendar,
  Folder, LinkIcon, Sparkles, Eye, Video, Send, Lock,
} from './icons';

const ASA_GROUP_ROUTE = '/messages';  // Messagerie interne ASA

const PHASE1_STEPS = [
  { id: 'rush_received',    label: 'Rush reçu',                              role: 'derush' },
  { id: 'derush_posted',    label: 'Dérush fait + posté sur le groupe ASA',  role: 'derush', opensGroup: true },
  { id: 'derush_validated', label: 'Validé',             role: 'validator' },
  { id: 'derush_uploaded',  label: 'Upload final sur Drive (clôture)',       role: 'derush' },
];

// Phase 2 = MONTAGE seul (jusqu'à frame_validated)
// Iter 62co — retrait du step "J'ai lu les consignes" (Rayan) : consignes lisibles direct
const PHASE2_STEPS = [
  { id: 'montage_finalized',  label: 'Montage terminé · envoyé sur le groupe', role: 'monteur', opensGroup: true },
  { id: 'frame_validated',    label: 'Frame validé',                          role: 'validator' },
];

// Phase 3 = PUBLICATION (miniature + vrai titre + description + publi)
const PHASE3_STEPS = [
  { id: 'thumbnail_url',      label: 'Miniature uploadée',                    role: 'derush_or_monteur' },
  { id: 'posted_online',      label: 'Vidéo publiée',                         role: 'monteur' },
];

// ============================================================================
// Drive matching helper (Iter41 — post-deploy fix)
// ----------------------------------------------------------------------------
// Rules per user spec (post-prod 30/02):
//   1. If task.rush_drive_url is set (founder override) → ALWAYS use it.
//   2. Else, match the project's drive_links by `format` field (exact match).
//   3. If no exact match, try matching by `name` containing the format keyword
//      (back-compat with older projects that don't have the format field).
//   4. If still no match → return null (NEVER fallback to drive_links[0]).
//      The UI MUST show a warning so the monteur knows the link is missing.
// ============================================================================
const FORMAT_LABELS = {
  podcast: 'Podcast',
  facecam: 'Facecam',
  short: 'Short',
  special: 'Spécial',
};

function resolveRushDrive(task, project) {
  if (task?.rush_drive_url) {
    return { url: task.rush_drive_url, name: 'Drive (override admin)', source: 'override' };
  }
  const fmt = (task?.video_format || 'special').toLowerCase();
  const links = project?.drive_links || [];
  if (!links.length) return null;
  // 1) exact format match
  const byFormat = links.find((l) => (l.format || '').toLowerCase() === fmt);
  if (byFormat) return { ...byFormat, source: 'format' };
  // 2) fallback : name contains format keyword (back-compat)
  const byName = links.find((l) => (l.name || '').toLowerCase().includes(fmt));
  if (byName) return { ...byName, source: 'name' };
  // 3) no match → null (caller shows warning)
  return null;
}

export { resolveRushDrive, FORMAT_LABELS };

export default function VideoPhaseWorkflow({ task, project, currentUser, users, onChanged }) {
  const [busyStep, setBusyStep] = useState(null);
  const [thumbInput, setThumbInput] = useState('');
  // Iter43 — active phase tab (must be declared before any early return per hooks rules)
  // Default is Phase 2 (Montage) — the most common view; useEffect below snaps to
  // the appropriate phase when task/visibility changes.
  const [activePhase, setActivePhase] = useState(2);
  // Iter43 — Messages popup state (in-place, no navigation loss)
  const [showMessagesPopup, setShowMessagesPopup] = useState(false);
  const navigate = useNavigate();

  const monteurId = task.assigned_to_montage || task.assigned_to;
  const derusheurId = task.assigned_to_derush;
  const isFounder = currentUser?.role === 'founder';
  const isValidator = isFounder || currentUser?.is_chef_monteur;
  const isMonteur = currentUser?.id === monteurId;
  const isDerusheur = currentUser?.id === derusheurId;

  const monteur = users.find((u) => u.id === monteurId);
  const derusheur = users.find((u) => u.id === derusheurId);

  const phase1Done = task.derush_uploaded;
  const fmt = (task.video_format || 'special').toLowerCase();

  // Iter43 — Compute Phase 1 visibility early (before any hooks) so the useEffect
  // below can react to it safely without violating hook order rules.
  const derushAccessGranted = (
    task.derush_access === true ||
    (Array.isArray(task.derush_access_users) && task.derush_access_users.includes(currentUser?.id))
  );
  const showPhase1 = isFounder || isDerusheur || derushAccessGranted;

  // On task/visibility change, snap to the appropriate default phase
  useEffect(() => {
    if (activePhase === 1 && !showPhase1) {
      setActivePhase(2);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPhase1, task.id]);

  const canFlip = (step) => {
    if (step.id === 'thumbnail_url') return isFounder || isMonteur || isDerusheur;
    if (step.role === 'validator') return isValidator;
    if (step.role === 'derush') return isFounder || isDerusheur;
    if (step.role === 'monteur') return isFounder || isMonteur;
    return isFounder;
  };

  // For each step, returns whether it can be flipped right now (ordering)
  const canFlipNow = (stepId, list) => {
    const idx = list.findIndex((s) => s.id === stepId);
    if (idx === 0) return true;
    for (let i = 0; i < idx; i++) {
      if (list[i].id === 'thumbnail_url') continue; // parallel
      if (!task[list[i].id]) return false;
    }
    return true;
  };

  const flipStep = async (stepId, value = true) => {
    setBusyStep(stepId);
    try {
      // Iter41 (post-deploy) — backend returns the updated task; pass it back to parent
      // so the UI re-renders the NEXT step immediately without any manual reload.
      const r = await api.post(`/tasks/${task.id}/phase-step`, { step: stepId, value });
      toast.success('Étape mise à jour');
      onChanged?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusyStep(null);
    }
  };

  // Iter41 (post-deploy) — founder toggle for "Miniature obligatoire" (veto)
  const toggleThumbnailRequired = async () => {
    const newVal = !(task.thumbnail_required ?? true);
    try {
      const r = await api.patch(`/tasks/${task.id}`, { thumbnail_required: newVal });
      toast.success(`Miniature ${newVal ? 'obligatoire' : 'optionnelle'}`);
      onChanged?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  // Iter41 (post-deploy) — founder override of the rush drive URL for THIS video.
  // Note : sending null gets filtered by the backend PATCH (v is not None). We send
  // empty string on clear — the UI treats empty string as "no override" (resolveRushDrive
  // checks truthiness).
  const saveRushDriveUrl = async (url) => {
    try {
      const r = await api.patch(`/tasks/${task.id}`, { rush_drive_url: url || '' });
      toast.success(url ? 'Drive overridé pour cette vidéo' : 'Override Drive retiré');
      onChanged?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  // Iter43 — founder toggle to grant Phase 1 (Dérush) visibility to the monteur.
  const toggleDerushAccess = async () => {
    const newVal = !(task.derush_access === true);
    try {
      const r = await api.patch(`/tasks/${task.id}`, { derush_access: newVal });
      toast.success(newVal ? 'Accès dérush accordé au monteur' : 'Accès dérush retiré');
      onChanged?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const uploadThumbnail = async () => {
    if (!thumbInput.trim()) { toast.error('Colle un lien'); return; }
    await flipStep('thumbnail_url', thumbInput.trim());
    setThumbInput('');
  };

  // ===== Render =====
  // === Iter34 : phase_stage='p1_only' OU dérusheur seul → afficher uniquement Phase 1
  const isPhase1Only = project?.phase_stage === 'p1_only';
  const isDerusheurOnly = isDerusheur && !isFounder && !isMonteur;
  if (isPhase1Only || isDerusheurOnly) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div data-testid="phase-indicator-derush" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 14px', borderRadius: 999,
          background: phase1Done ? 'rgba(52,211,153,0.15)' : 'rgba(91,190,252,0.15)',
          border: '1.5px solid ' + (phase1Done ? '#34D399' : '#5BBEFC'),
          color: phase1Done ? '#34D399' : '#5BBEFC',
          fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
          width: 'fit-content',
        }}>
          {phase1Done ? <CheckCircle size={11} /> : <Folder size={11} />}
          {phase1Done ? '✓ Phase 1 terminée' : 'Phase 1 — Dérush'}
          {task.derush_deadline && !phase1Done && (
            <span style={{ marginLeft: 6, color: 'var(--t3)', fontWeight: 500 }}>
              · avant {new Date(task.derush_deadline).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        {!phase1Done && <DerushBrief task={task} project={project} fmt={fmt} />}
        <PhaseSection
          title="Phase 1 — Dérush"
          subtitle={derusheur ? `Dérusheur : ${derusheur.name}` : ''}
          color="#5BBEFC"
          done={phase1Done}
        >
          {PHASE1_STEPS.map((step) => (
            <StepRow
              key={step.id}
              step={step}
              task={task}
              allow={canFlip(step) && canFlipNow(step.id, PHASE1_STEPS)}
              busy={busyStep === step.id}
              onFlip={() => flipStep(step.id, !task[step.id])}
              onOpenGroup={() => setShowMessagesPopup(true)}
            />
          ))}
        </PhaseSection>
        {phase1Done && isDerusheurOnly && !isPhase1Only && (
          <div data-testid="derush-done-msg" style={{
            padding: '14px 16px', borderRadius: 12,
            background: 'rgba(52,211,153,0.10)',
            border: '1.5px solid #34D399',
            fontFamily: 'Lexend', fontSize: 13, color: '#34D399',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <CheckCircle size={18} />
            <div>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13 }}>Dérush terminé</div>
              <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
                Le monteur prend le relais. Tu peux passer à la prochaine vidéo.
              </div>
            </div>
          </div>
        )}
        {/* === Iter34 : si projet p1_only ET phase 1 terminée → CTA "Passer en Phase 2" pour founder */}
        {phase1Done && isPhase1Only && isFounder && (
          <div data-testid="phase2-cta-banner" style={{
            padding: '16px 18px', borderRadius: 14,
            background: 'linear-gradient(135deg, rgba(167,139,250,0.12), rgba(91,190,252,0.10))',
            border: '1.5px solid rgba(167,139,250,0.45)',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <CheckCircle size={22} color="#34D399" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)' }}>
                Phase 1 terminée — Passer à la Phase 2 ?
              </div>
              <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 3 }}>
                Pose ta fiche montage (manuel ou doc IA) pour démarrer le montage.
              </div>
            </div>
            <button
              data-testid="phase2-cta-btn"
              onClick={() => navigate(`/projects?phase2=${project.id}`)}
              className="asa-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              Lancer la Phase 2 →
            </button>
          </div>
        )}
        {phase1Done && isPhase1Only && !isFounder && (
          <div data-testid="phase1-only-done-msg" style={{
            padding: '14px 16px', borderRadius: 12,
            background: 'rgba(52,211,153,0.10)',
            border: '1.5px solid #34D399',
            fontFamily: 'Lexend', fontSize: 13, color: '#34D399',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <CheckCircle size={18} />
            <div>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13 }}>Phase 1 terminée</div>
              <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
                En attente du founder pour démarrer la Phase 2.
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const isYoutube = (project?.content_type || 'short') === 'youtube';

  // Iter43 — Phase-by-phase full-screen view : one phase visible at a time.
  // (showPhase1 + useEffect activePhase snap are declared earlier — hooks order.)
  // Iter 62cp — plus AUCUN cadenas : toutes les phases sont accessibles à tout moment
  const phaseTabs = [
    ...(showPhase1 ? [{ id: 1, label: 'Phase 1 · Dérush', color: '#5BBEFC', done: phase1Done }] : []),
    { id: 2, label: 'Montage', color: '#A78BFA', done: task.stage === 'done' || task.stage === 'to_post' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Iter43g — Founder task metadata edit (title, priority, deadline, assignments, format) */}
      {isFounder && (
        <TaskMetaEditor task={task} project={project} users={users} onSaved={onChanged} />
      )}
      {/* Phase indicator pill */}
      <div data-testid="phase-indicator" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 14px', borderRadius: 999,
        background: phase1Done ? 'rgba(167,139,250,0.15)' : 'rgba(91,190,252,0.15)',
        border: '1.5px solid ' + (phase1Done ? '#A78BFA' : '#5BBEFC'),
        color: phase1Done ? '#A78BFA' : '#5BBEFC',
        fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
        width: 'fit-content',
      }}>
        {phase1Done ? <Video size={11} /> : <Folder size={11} />}
        Phase {task.phase || 1} · {phase1Done ? 'Montage' : 'Dérush'}
        {task.derush_deadline && !phase1Done && (
          <span style={{ marginLeft: 6, color: 'var(--t3)', fontWeight: 500 }}>
            · avant {new Date(task.derush_deadline).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {/* ============================================ FOUNDER ADMIN PANEL (Iter41 post-deploy) */}
      {isFounder && (
        <FounderAdminPanel
          task={task}
          project={project}
          fmt={fmt}
          onToggleThumb={toggleThumbnailRequired}
          onSaveDrive={saveRushDriveUrl}
          onToggleDerushAccess={toggleDerushAccess}
        />
      )}

      {/* ============================================ Iter43 — Phase Tabs (full-screen 1 phase at a time) */}
      <div data-testid="phase-tabs" style={{
        display: 'flex', gap: 6, padding: 4,
        background: 'var(--sf2)', borderRadius: 12,
        border: '1px solid var(--bd)',
      }}>
        {phaseTabs.map((tab) => {
          const active = activePhase === tab.id;
          return (
            <button
              key={tab.id}
              data-testid={`phase-tab-${tab.id}`}
              type="button"
              onClick={() => setActivePhase(tab.id)}
              disabled={tab.locked}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 8,
                background: active ? `${tab.color}22` : 'transparent',
                border: `1.5px solid ${active ? tab.color : 'transparent'}`,
                color: active ? tab.color : (tab.locked ? 'var(--t3)' : 'var(--t2)'),
                fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                cursor: tab.locked ? 'not-allowed' : 'pointer',
                opacity: tab.locked ? 0.5 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'all 0.15s',
              }}
            >
              {tab.done && <CheckCircle size={12} color={tab.color} />}
              {tab.locked && <Lock size={11} />}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ============================================ Iter43 — Active phase (full-width) */}
      <div data-testid={`phase-content-${activePhase}`} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {activePhase === 1 && showPhase1 && (
          <>
            {!phase1Done && <DerushBrief task={task} project={project} fmt={fmt} />}
            <PhaseSection
              title="Phase 1 — Dérush"
              subtitle={derusheur ? `Dérusheur : ${derusheur.name}` : ''}
              color="#5BBEFC"
              done={phase1Done}
            >
              {PHASE1_STEPS.map((step) => (
                <StepRow
                  key={step.id}
                  step={step}
                  task={task}
                  allow={canFlip(step) && canFlipNow(step.id, PHASE1_STEPS)}
                  busy={busyStep === step.id}
                  onFlip={() => flipStep(step.id, !task[step.id])}
                  onOpenGroup={() => setShowMessagesPopup(true)}
                />
              ))}
            </PhaseSection>
          </>
        )}

        {activePhase === 2 && (
          <>
            <ConsignesBlock task={task} project={project} fmt={fmt} locked={false} />
            <PublicationCopyBlock task={task} canEdit={isFounder || currentUser?.role === 'gestionnaire'} onSaved={onChanged} />
          </>
        )}

      </div>

      {/* Iter43 — Messages popup (opens in-place, close X keeps user on project) */}
      {showMessagesPopup && (
        <MessagesPopup onClose={() => setShowMessagesPopup(false)} />
      )}
    </div>
  );
}

// ============================================================================
// Iter43g — TaskMetaEditor : founder-only inline metadata editor for a video task
// Lets the founder change title, priority, deadline, monteur, dérusheur, format
// directly from the task drawer (no need to leave /tasks). Collapsible.
// ============================================================================
function TaskMetaEditor({ task, project, users, onSaved }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: task.title || '',
    priority: task.priority || 'medium',
    due_date: (task.due_date || '').slice(0, 10),
    assigned_to_montage: task.assigned_to_montage || '',
    assigned_to_derush: task.assigned_to_derush || '',
    video_format: task.video_format || 'special',
    description: task.description || '',
  });

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        title: form.title,
        priority: form.priority,
        due_date: form.due_date || null,
        assigned_to_montage: form.assigned_to_montage || null,
        assigned_to_derush: form.assigned_to_derush || null,
        video_format: form.video_format,
        description: form.description,
      };
      const r = await api.patch(`/tasks/${task.id}`, patch);
      toast.success('Tâche modifiée');
      onSaved?.(r.data);
      setExpanded(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const monteurs = (users || []).filter((u) => u.role === 'contributor' || u.role === 'founder' || u.is_monteur === true);
  const formatOpts = [
    ['podcast', 'Podcast'], ['facecam', 'Facecam'],
    ['short', 'Short'], ['special', 'Spécial'],
  ];

  return (
    <div data-testid="task-meta-editor" style={{
      padding: '10px 14px', borderRadius: 12,
      background: 'rgba(245,158,11,0.06)',
      border: '1.5px solid rgba(245,158,11,0.28)',
    }}>
      <button
        type="button"
        data-testid="task-meta-toggle"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, color: '#F59E0B' }}>
          ✎ Modifier la tâche
        </span>
        <span style={{ marginLeft: 'auto', color: '#F59E0B', fontFamily: 'Sora', fontWeight: 700, fontSize: 11 }}>
          {expanded ? '▴' : '▾'}
        </span>
      </button>
      {expanded && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
          <MetaField label="Titre" full>
            <input
              data-testid="task-meta-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </MetaField>
          <MetaField label="Priorité">
            <select
              data-testid="task-meta-priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            >
              <option value="high">🔴 Haute</option>
              <option value="medium">🟡 Moyenne</option>
              <option value="low">🟢 Basse</option>
            </select>
          </MetaField>
          <MetaField label="Deadline">
            <input
              data-testid="task-meta-due"
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            />
          </MetaField>
          <MetaField label="Monteur">
            <select
              data-testid="task-meta-monteur"
              value={form.assigned_to_montage}
              onChange={(e) => setForm({ ...form, assigned_to_montage: e.target.value })}
            >
              <option value="">— aucun —</option>
              {monteurs.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </MetaField>
          <MetaField label="Dérusheur">
            <select
              data-testid="task-meta-derusheur"
              value={form.assigned_to_derush}
              onChange={(e) => setForm({ ...form, assigned_to_derush: e.target.value })}
            >
              <option value="">— aucun —</option>
              {monteurs.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </MetaField>
          <MetaField label="Format">
            <select
              data-testid="task-meta-format"
              value={form.video_format}
              onChange={(e) => setForm({ ...form, video_format: e.target.value })}
            >
              {formatOpts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </MetaField>
          <MetaField label="Description" full>
            <textarea
              data-testid="task-meta-description"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </MetaField>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              style={{ padding: '6px 12px', fontSize: 11, color: 'var(--t3)', cursor: 'pointer' }}
            >Annuler</button>
            <button
              data-testid="task-meta-save"
              type="button"
              onClick={save}
              disabled={busy}
              className="asa-btn"
              style={{ padding: '6px 14px', fontSize: 11 }}
            >{busy ? '…' : 'Enregistrer'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MetaField({ label, children, full }) {
  return (
    <label style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      gridColumn: full ? '1 / -1' : 'auto',
    }}>
      <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
      <div className="asa-field" style={{ padding: '7px 10px' }}>
        {children}
      </div>
    </label>
  );
}

// ============================================================================
function PhaseSection({ title, subtitle, color, done, locked, lockedMessage, children }) {
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 14,
      background: 'var(--sf2)',
      border: `1.5px solid ${done ? color : 'var(--bd)'}`,
      opacity: locked ? 0.55 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: done ? color : 'var(--bd)',
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: 'var(--t1)' }}>{title}</div>
          {subtitle && (
            <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        {done && <CheckCircle size={16} color={color} />}
      </div>
      {locked && lockedMessage && (
        <div style={{
          fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)',
          padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)',
          marginBottom: 8,
        }}>
          🔒 {lockedMessage}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

// ============================================================================
function StepRow({ step, task, allow, busy, onFlip, accent = '#5BBEFC', onOpenGroup }) {
  const checked = !!task[step.id];
  const handleClick = () => {
    if (!allow) return;
    if (step.opensGroup && !checked && onOpenGroup) {
      onOpenGroup();
    }
    onFlip();
  };
  return (
    <button
      data-testid={`phase-step-${step.id}`}
      type="button"
      onClick={handleClick}
      disabled={!allow}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderRadius: 10,
        background: checked ? `${accent}22` : 'transparent',
        border: `1.5px solid ${checked ? accent : 'var(--bd)'}`,
        cursor: allow ? 'pointer' : 'not-allowed',
        opacity: allow ? 1 : 0.55,
        textAlign: 'left',
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: 6,
        background: checked ? accent : 'transparent',
        border: `1.5px solid ${checked ? accent : 'var(--bd)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {busy ? <Loader2 size={11} className="spin" color="#fff" /> :
          checked ? <Check size={11} color="#fff" /> : null}
      </div>
      <div style={{
        flex: 1, fontFamily: 'Lexend', fontSize: 12.5,
        color: checked ? accent : 'var(--t2)',
        fontWeight: checked ? 600 : 500,
      }}>{step.label}</div>
      {step.opensGroup && !checked && allow && (
        <Send size={11} color={accent} />
      )}
    </button>
  );
}

// ============================================================================
function ThumbnailRow({ task, allow, busy, value, onChange, onUpload }) {
  const accent = '#A78BFA';
  const done = !!task.thumbnail_url;
  if (done) {
    return (
      <div data-testid="phase-step-thumbnail_url" style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderRadius: 10,
        background: `${accent}22`,
        border: `1.5px solid ${accent}`,
      }}>
        <CheckCircle size={16} color={accent} />
        <div style={{ flex: 1, fontFamily: 'Lexend', fontSize: 12.5, color: accent, fontWeight: 600 }}>
          Miniature uploadée
        </div>
        <a
          data-testid="thumbnail-url-link"
          href={task.thumbnail_url} target="_blank" rel="noreferrer"
          style={{ color: accent, fontSize: 12 }}
        >Voir</a>
      </div>
    );
  }
  if (!allow) {
    return (
      <div data-testid="phase-step-thumbnail_url" style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderRadius: 10,
        background: 'transparent', border: '1.5px solid var(--bd)',
        opacity: 0.55,
      }}>
        <div style={{
          width: 18, height: 18, borderRadius: 6, border: '1.5px solid var(--bd)', flexShrink: 0,
        }} />
        <div style={{ flex: 1, fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)' }}>
          En attente : miniature à uploader (dérusheur ou monteur)
        </div>
      </div>
    );
  }
  return (
    <div style={{
      padding: '9px 12px', borderRadius: 10, background: 'transparent',
      border: '1.5px dashed var(--bd)',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <Upload size={14} color="var(--t3)" />
      <input
        data-testid="thumbnail-url-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Colle le lien de la miniature (Drive/image)"
        style={{
          flex: 1, fontSize: 12.5, padding: '5px 8px',
          background: 'transparent', border: 'none', color: 'var(--t1)',
          fontFamily: 'Lexend', outline: 'none',
        }}
      />
      <button
        data-testid="thumbnail-upload-btn"
        type="button" onClick={onUpload} disabled={busy || !value.trim()}
        style={{
          background: '#A78BFA', border: 'none', borderRadius: 8,
          color: '#fff', fontFamily: 'Sora', fontWeight: 700,
          fontSize: 12, padding: '6px 12px',
          cursor: busy ? 'not-allowed' : 'pointer',
        }}
      >{busy ? <Loader2 size={11} className="spin" /> : 'Upload'}</button>
    </div>
  );
}

// ============================================================================
// FounderAdminPanel (Iter41 post-deploy)
// ----------------------------------------------------------------------------
// Founder-only sticky controls at the top of the video workflow:
//   - Rush Drive URL override (per video) — editable inline, saves to backend.
//   - Thumbnail required veto — ON/OFF toggle. When OFF, Phase 3 can complete
//     without uploading a thumbnail.
// ============================================================================
function FounderAdminPanel({ task, project, fmt, onToggleThumb, onSaveDrive, onToggleDerushAccess }) {
  const matched = useMemo(() => resolveRushDrive(task, project), [task, project]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.rush_drive_url || '');
  const thumbRequired = task.thumbnail_required ?? true;
  const derushAccess = task.derush_access === true;

  return (
    <div data-testid="founder-admin-panel" style={{
      padding: '12px 14px', borderRadius: 12,
      background: 'rgba(251,191,36,0.06)',
      border: '1.5px solid rgba(251,191,36,0.35)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div className="asa-eyebrow" style={{ color: '#FBBF24', fontSize: 10 }}>
        ⚙ Admin · Founder only
      </div>

      {/* Drive override */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', minWidth: 110 }}>
          Drive de rush :
        </div>
        {!editing ? (
          <>
            <span style={{ fontFamily: 'Lexend', fontSize: 12, color: matched ? 'var(--t1)' : '#FBBF24', flex: 1, wordBreak: 'break-all' }}>
              {matched ? (
                <>
                  {matched.name}
                  <span style={{ marginLeft: 6, color: 'var(--t3)', fontSize: 10 }}>
                    (source : {matched.source === 'override' ? 'override admin' : matched.source === 'format' ? 'format match' : 'name match'})
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                  Aucun Drive pour le format <strong>{FORMAT_LABELS[fmt] || fmt}</strong>
                </>
              )}
            </span>
            <button
              data-testid="founder-edit-drive-btn"
              type="button"
              onClick={() => { setDraft(task.rush_drive_url || ''); setEditing(true); }}
              className="asa-btn-ghost"
              style={{ fontSize: 11, padding: '5px 10px' }}
            >
              {task.rush_drive_url ? 'Modifier' : 'Override'}
            </button>
          </>
        ) : (
          <>
            <input
              data-testid="founder-drive-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="https://drive.google.com/..."
              style={{ flex: 1, minWidth: 200, fontSize: 12, padding: '6px 8px' }}
            />
            <button
              data-testid="founder-save-drive-btn"
              type="button"
              onClick={async () => { await onSaveDrive(draft.trim()); setEditing(false); }}
              className="asa-btn"
              style={{ fontSize: 11, padding: '5px 10px' }}
            >
              Enregistrer
            </button>
            {task.rush_drive_url && (
              <button
                data-testid="founder-clear-drive-btn"
                type="button"
                onClick={async () => { setDraft(''); await onSaveDrive(''); setEditing(false); }}
                className="asa-btn-ghost"
                style={{ fontSize: 11, padding: '5px 10px', color: '#FF6B6B' }}
              >
                Retirer
              </button>
            )}
            <button
              type="button"
              onClick={() => { setEditing(false); setDraft(task.rush_drive_url || ''); }}
              className="asa-btn-ghost"
              style={{ fontSize: 11, padding: '5px 10px' }}
            >
              Annuler
            </button>
          </>
        )}
      </div>

      {/* Thumbnail required toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', minWidth: 110 }}>
          Miniature obligatoire :
        </div>
        <button
          data-testid="founder-thumb-required-toggle"
          type="button"
          onClick={onToggleThumb}
          aria-pressed={thumbRequired}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', borderRadius: 999,
            background: thumbRequired ? 'rgba(52,211,153,0.18)' : 'rgba(148,163,184,0.18)',
            border: '1.5px solid ' + (thumbRequired ? '#34D399' : 'rgba(148,163,184,0.55)'),
            color: thumbRequired ? '#34D399' : 'var(--t3)',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >
          <span style={{
            width: 22, height: 12, borderRadius: 999, position: 'relative',
            background: thumbRequired ? '#34D399' : 'rgba(148,163,184,0.55)',
            transition: 'background 0.15s',
          }}>
            <span style={{
              position: 'absolute', top: 1, left: thumbRequired ? 12 : 1,
              width: 10, height: 10, borderRadius: '50%',
              background: 'white', transition: 'left 0.15s',
            }} />
          </span>
          {thumbRequired ? 'ON · veto activé' : 'OFF · publication libre'}
        </button>
      </div>

      {/* Iter43 — Grant Phase 1 (Dérush) visibility to the monteur */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', minWidth: 110 }}>
          Accès dérush monteur :
        </div>
        <button
          data-testid="founder-derush-access-toggle"
          type="button"
          onClick={onToggleDerushAccess}
          aria-pressed={derushAccess}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', borderRadius: 999,
            background: derushAccess ? 'rgba(91,190,252,0.18)' : 'rgba(148,163,184,0.18)',
            border: '1.5px solid ' + (derushAccess ? '#5BBEFC' : 'rgba(148,163,184,0.55)'),
            color: derushAccess ? '#5BBEFC' : 'var(--t3)',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          <span style={{
            width: 22, height: 12, borderRadius: 999, position: 'relative',
            background: derushAccess ? '#5BBEFC' : 'rgba(148,163,184,0.55)',
            transition: 'background 0.15s',
          }}>
            <span style={{
              position: 'absolute', top: 1, left: derushAccess ? 12 : 1,
              width: 10, height: 10, borderRadius: '50%',
              background: 'white', transition: 'left 0.15s',
            }} />
          </span>
          {derushAccess ? 'ON · monteur voit Phase 1' : 'OFF · Phase 1 cachée'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
function DerushBrief({ task, project, fmt }) {
  // Iter41 (post-deploy) — resolve drive by FORMAT (not position).
  // If no drive matches the format, we display a warning chip instead of a
  // misleading fallback link.
  const rushLink = useMemo(() => resolveRushDrive(task, project), [task, project]);
  // Also expose SwissTransfer links (some projects send rushs via WeTransfer/Swift)
  const swiftLinks = project?.swift_links || [];

  return (
    <div data-testid="derush-brief" style={{
      padding: '14px 16px', borderRadius: 14,
      background: 'rgba(91,190,252,0.06)',
      border: '1.5px solid rgba(91,190,252,0.40)',
    }}>
      <div className="asa-eyebrow" style={{ marginBottom: 10, color: '#5BBEFC' }}>
        <Folder size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
        À dérusher · {FORMAT_LABELS[fmt] || fmt}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '6px 12px', fontFamily: 'Lexend', fontSize: 12.5 }}>
        <span style={{ color: 'var(--t3)' }}>Vidéo :</span>
        <strong style={{ color: 'var(--t1)' }}>{task.title}</strong>

        <span style={{ color: 'var(--t3)' }}>Client :</span>
        <strong style={{ color: 'var(--t1)' }}>{project?.client || project?.name || '—'}</strong>

        {task.derush_deadline && (<>
          <span style={{ color: 'var(--t3)' }}>Avant le :</span>
          <strong style={{ color: '#FF6B6B' }}>
            <Calendar size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
            {new Date(task.derush_deadline).toLocaleString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </strong>
        </>)}

        <span style={{ color: 'var(--t3)' }}>Rush :</span>
        {rushLink ? (
          <a data-testid="derush-rush-link"
            href={rushLink.url} target="_blank" rel="noreferrer"
            style={{ color: '#5BBEFC', wordBreak: 'break-all' }}
          >
            <LinkIcon size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
            {rushLink.name}
            {rushLink.source === 'override' && (
              <span style={{ marginLeft: 6, fontSize: 10, color: '#FBBF24' }}>· override</span>
            )}
          </a>
        ) : (
          <span data-testid="derush-rush-missing" style={{
            color: '#FBBF24', fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5,
          }}>
            <AlertTriangle size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
            Aucun Drive pour le format <strong>{FORMAT_LABELS[fmt] || fmt}</strong> — demande au founder de l&apos;ajouter
          </span>
        )}
        {swiftLinks.map((l, i) => (
          <Fragment key={i}>
            <span style={{ color: 'var(--t3)' }}>{i === 0 ? 'SwissTransfer :' : ''}</span>
            <a href={l.url} target="_blank" rel="noreferrer" style={{ color: '#5BBEFC', wordBreak: 'break-all' }}>
              <LinkIcon size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
              {l.name || l.url}
            </a>
          </Fragment>
        ))}
      </div>
      <div style={{
        marginTop: 12, padding: '8px 12px', borderRadius: 10,
        background: 'rgba(91,190,252,0.10)',
        fontFamily: 'Lexend', fontSize: 11.5, color: '#5BBEFC',
      }}>
        💡 Coche les 4 étapes ci-dessous au fil du dérush.
      </div>
    </div>
  );
}

// ============================================================================
function ConsignesBlock({ task, project, fmt, locked }) {
  // Iter 62co — retrait de la gate "J'ai lu les consignes" : consignes + drive
  // affichés directement dès l'ouverture de la Phase 2 (demande Rayan).
  const consignesRead = true;
  // Iter41 (post-deploy) — drive matched by format (no positional fallback)
  const rushLink = useMemo(() => resolveRushDrive(task, project), [task, project]);

  return (
    <div data-testid="consignes-block" style={{
      padding: '14px 16px', borderRadius: 14,
      background: 'rgba(167,139,250,0.06)',
      border: '1.5px solid ' + (consignesRead ? '#A78BFA' : 'rgba(167,139,250,0.45)'),
      opacity: locked ? 0.55 : 1,
    }}>
      <div className="asa-eyebrow" style={{ marginBottom: 10, color: '#A78BFA' }}>
        <Eye size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
        Consignes du montage
      </div>

      {/* === BLOC FUSIONNÉ : tout ce que le monteur doit savoir AVANT de monter === */}
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '6px 12px', fontFamily: 'Lexend', fontSize: 12.5 }}>
        <span style={{ color: 'var(--t3)' }}>Titre :</span>
        <strong style={{ color: 'var(--t1)' }}>{task.title}</strong>

        <span style={{ color: 'var(--t3)' }}>Format :</span>
        <strong style={{ color: 'var(--t1)' }}>{FORMAT_LABELS[fmt] || fmt}</strong>

        <span style={{ color: 'var(--t3)' }}>Client :</span>
        <strong style={{ color: 'var(--t1)' }}>{project?.client || project?.name || '—'}</strong>

        <span style={{ color: 'var(--t3)' }}>Échéance :</span>
        <strong style={{ color: 'var(--t1)' }}>
          {task.due_date
            ? formatDay(task.due_date, { weekday: 'short', day: 'numeric', month: 'short' })
            : '—'}
        </strong>

        {task.description && <>
          <span style={{ color: 'var(--t3)' }}>Indications :</span>
          <span style={{ color: 'var(--t2)', whiteSpace: 'pre-wrap' }}>{task.description}</span>
        </>}

        {task.inspiration_link && <>
          <span style={{ color: 'var(--t3)' }}>Inspi :</span>
          <a data-testid="consignes-inspi-link"
            href={task.inspiration_link} target="_blank" rel="noreferrer"
            style={{ color: '#5BBEFC', wordBreak: 'break-all' }}>{task.inspiration_link}</a>
        </>}

        {task.react_link && <>
          <span style={{ color: 'var(--t3)' }}>React :</span>
          <a data-testid="consignes-react-link"
            href={task.react_link} target="_blank" rel="noreferrer"
            style={{ color: '#F472B6', wordBreak: 'break-all' }}>{task.react_link}</a>
        </>}
      </div>

      {/* === DRIVE link CACHÉ tant que pas de "J'ai lu les consignes" === */}
      <div style={{ marginTop: 14 }}>
        {!consignesRead ? (
          <div style={{
            padding: '10px 12px', borderRadius: 10,
            background: 'rgba(255,255,255,0.04)',
            border: '1.5px dashed var(--bd)',
            fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            🔒 Lis et valide les consignes ci-dessus pour révéler le lien du Drive à monter.
          </div>
        ) : rushLink ? (
          <div data-testid="consignes-drive-revealed" style={{
            padding: '12px 14px', borderRadius: 12,
            background: 'rgba(91,190,252,0.10)',
            border: '1.5px solid #5BBEFC',
          }}>
            <div className="asa-eyebrow" style={{ marginBottom: 6, color: '#5BBEFC' }}>
              <Folder size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
              Vidéo {(task.video_idx ?? 0) + 1} à monter · format <strong>{FORMAT_LABELS[fmt] || fmt}</strong>
              {rushLink.source === 'override' && (
                <span style={{ marginLeft: 6, fontSize: 10, color: '#FBBF24' }}>· override admin</span>
              )}
            </div>
            <a data-testid="consignes-drive-link"
              href={rushLink.url} target="_blank" rel="noreferrer"
              style={{
                color: '#5BBEFC', fontFamily: 'Lexend', fontSize: 13,
                fontWeight: 600, wordBreak: 'break-all',
              }}>
              <LinkIcon size={12} style={{ verticalAlign: '-1px', marginRight: 5 }} />
              {rushLink.name}
            </a>
          </div>
        ) : (
          <div data-testid="consignes-drive-missing" style={{
            padding: '10px 12px', borderRadius: 10,
            background: 'rgba(251,191,36,0.10)',
            border: '1.5px solid #FBBF24',
            fontFamily: 'Lexend', fontSize: 12, color: '#FBBF24',
          }}>
            <AlertTriangle size={12} style={{ verticalAlign: '-1px', marginRight: 5 }} />
            Aucun Drive de rush pour le format <strong>{FORMAT_LABELS[fmt] || fmt}</strong>.
            Le founder doit l&apos;ajouter au projet ou définir un override sur cette vidéo.
          </div>
        )}
      </div>

      {/* Iter 62co — bandeau "Consignes lues" retiré : plus de gate à valider */}

      {/* === Consignes techniques (collapsible) === */}
      <TechnicalNotes task={task} />
    </div>
  );
}

// ============================================================================
function TechnicalNotes({ task }) {
  const [open, setOpen] = useState(true);
  const hasContent = task.inspiration_link || task.tech_instructions;
  if (!hasContent) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <button
        data-testid="tech-notes-toggle"
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 10,
          background: 'transparent', border: '1.5px solid var(--bd)',
          color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
          display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
        }}
      >
        <span style={{ transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        Consignes techniques
      </button>
      {open && (
        <div data-testid="tech-notes-body" style={{
          marginTop: 6, padding: '12px 14px', borderRadius: 10,
          background: 'var(--sf2)', border: '1px solid var(--bd)',
          fontFamily: 'Lexend', fontSize: 12.5,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {task.inspiration_link && (
            <div>
              <div style={{ color: 'var(--t3)', fontSize: 11, marginBottom: 3 }}>Lien d&apos;inspiration</div>
              <a href={task.inspiration_link} target="_blank" rel="noreferrer"
                style={{ color: '#5BBEFC', wordBreak: 'break-all' }}>
                <LinkIcon size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                {task.inspiration_link}
              </a>
            </div>
          )}
          {task.tech_instructions && (
            <div>
              <div style={{ color: 'var(--t3)', fontSize: 11, marginBottom: 3 }}>Directives techniques</div>
              <div style={{ color: 'var(--t2)', whiteSpace: 'pre-wrap' }}>{task.tech_instructions}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================================
function PublicationBlock({ task, project, isFounder, allowUpload, allowPost, busyStep, thumbInput, setThumbInput, onUploadThumb, onPost }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [pubTitle, setPubTitle] = useState(task.publication_title || '');
  const [pubDesc, setPubDesc] = useState(task.publication_description || '');
  const [saving, setSaving] = useState(null);

  const savePubField = async (field, value) => {
    setSaving(field);
    try {
      await api.patch(`/tasks/${task.id}`, { [field]: value });
      toast.success('Enregistré');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(null); }
  };

  // Phase 3 = unblocked ONLY when frame_validated
  const frameValid = !!task.frame_validated;
  const hasThumb = !!task.thumbnail_url;

  return (
    <div data-testid="publication-block" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* === MINIATURE === */}
      <div style={{
        padding: '11px 13px', borderRadius: 11,
        background: hasThumb ? 'rgba(52,211,153,0.12)' : 'var(--sf2)',
        border: '1.5px solid ' + (hasThumb ? '#34D399' : 'var(--bd)'),
      }}>
        <div className="asa-eyebrow" style={{ marginBottom: 6, color: hasThumb ? '#34D399' : 'var(--t3)' }}>
          <Upload size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
          Miniature
        </div>
        {hasThumb ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle size={14} color="#34D399" />
            <a data-testid="thumbnail-url-link" href={task.thumbnail_url}
              target="_blank" rel="noreferrer"
              style={{ color: '#34D399', fontFamily: 'Lexend', fontSize: 12.5, wordBreak: 'break-all' }}
            >{task.thumbnail_url}</a>
          </div>
        ) : allowUpload ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              data-testid="thumbnail-url-input"
              value={thumbInput} onChange={(e) => setThumbInput(e.target.value)}
              placeholder="Colle le lien de la miniature"
              style={{
                flex: 1, fontSize: 12.5, padding: '6px 10px',
                background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 8,
                color: 'var(--t1)', fontFamily: 'Lexend',
              }}
            />
            <button
              data-testid="thumbnail-upload-btn"
              type="button" onClick={onUploadThumb}
              disabled={busyStep === 'thumbnail_url' || !thumbInput.trim()}
              style={{
                background: '#34D399', border: 'none', borderRadius: 8,
                color: '#fff', fontFamily: 'Sora', fontWeight: 700,
                fontSize: 12, padding: '6px 14px',
                cursor: busyStep === 'thumbnail_url' ? 'not-allowed' : 'pointer',
              }}
            >{busyStep === 'thumbnail_url' ? <Loader2 size={11} className="spin" /> : 'Upload'}</button>
          </div>
        ) : (
          <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)' }}>
            En attente : à uploader par le dérusheur ou le monteur.
          </div>
        )}
      </div>

      {/* === VRAI TITRE & DESCRIPTION === */}
      <div style={{
        padding: '11px 13px', borderRadius: 11,
        background: 'var(--sf2)', border: '1.5px solid var(--bd)',
      }}>
        <div className="asa-eyebrow" style={{ marginBottom: 8, color: 'var(--t3)' }}>
          📋 Fiche de post
        </div>
        {!isFounder && (
          <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginBottom: 8 }}>
            Titre et description modifiables uniquement par un admin.
          </div>
        )}
        <div style={{ marginBottom: 8 }}>
          <div className="asa-eyebrow" style={{ marginBottom: 4 }}>Vrai titre de la vidéo (différent du nom Drive)</div>
          {editingTitle && isFounder ? (
            <input
              data-testid="pub-title-input"
              autoFocus value={pubTitle}
              onChange={(e) => setPubTitle(e.target.value)}
              onBlur={() => { setEditingTitle(false); if (pubTitle !== task.publication_title) savePubField('publication_title', pubTitle); }}
              placeholder="Ex: J'ai testé X pendant 30 jours…"
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 8, color: 'var(--t1)', fontFamily: 'Lexend' }}
            />
          ) : (
            <div
              data-testid="pub-title-display"
              onClick={() => isFounder && setEditingTitle(true)}
              style={{
                padding: '7px 10px', background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 8,
                fontFamily: 'Lexend', fontSize: 13, color: pubTitle ? 'var(--t1)' : 'var(--t3)',
                cursor: isFounder ? 'text' : 'default',
                minHeight: 18,
              }}
            >{pubTitle || (isFounder ? '— Clique pour écrire le titre publié —' : '—')}</div>
          )}
        </div>
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 4 }}>Description</div>
          {editingDesc && isFounder ? (
            <textarea
              data-testid="pub-desc-input"
              autoFocus value={pubDesc}
              onChange={(e) => setPubDesc(e.target.value)}
              onBlur={() => { setEditingDesc(false); if (pubDesc !== task.publication_description) savePubField('publication_description', pubDesc); }}
              rows={3} placeholder="Description à coller sous la vidéo…"
              style={{ width: '100%', fontSize: 12.5, padding: '7px 10px', background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 8, color: 'var(--t1)', fontFamily: 'Lexend', resize: 'vertical' }}
            />
          ) : (
            <div
              data-testid="pub-desc-display"
              onClick={() => isFounder && setEditingDesc(true)}
              style={{
                padding: '7px 10px', background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 8,
                fontFamily: 'Lexend', fontSize: 12.5, color: pubDesc ? 'var(--t2)' : 'var(--t3)',
                cursor: isFounder ? 'text' : 'default', whiteSpace: 'pre-wrap',
                minHeight: 36,
              }}
            >{pubDesc || (isFounder ? '— Clique pour écrire la description —' : '—')}</div>
          )}
        </div>
        {saving && (
          <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 4 }}>
            <Loader2 size={9} className="spin" /> Enregistrement…
          </div>
        )}
      </div>

      {/* === BOUTON PUBLIER (final) === */}
      <button
        data-testid="phase-step-posted_online"
        type="button"
        onClick={allowPost && hasThumb ? onPost : undefined}
        disabled={!allowPost || !hasThumb || busyStep === 'posted_online'}
        style={{
          padding: '11px 16px', borderRadius: 12,
          background: task.posted_online ? '#34D39922' : (allowPost && hasThumb ? '#34D399' : 'var(--sf2)'),
          border: task.posted_online ? '1.5px solid #34D399' : '1.5px solid ' + (allowPost && hasThumb ? '#34D399' : 'var(--bd)'),
          color: task.posted_online ? '#34D399' : (allowPost && hasThumb ? '#fff' : 'var(--t3)'),
          fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
          cursor: (allowPost && hasThumb && !task.posted_online) ? 'pointer' : 'not-allowed',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {busyStep === 'posted_online' ? <Loader2 size={13} className="spin" /> : (task.posted_online ? <CheckCircle size={13} /> : <Send size={13} />)}
        {task.posted_online ? 'Vidéo publiée' : (allowPost && hasThumb ? 'Publier la vidéo' : 'Publier')}
      </button>
    </div>
  );
}


// ============================================================================
// Iter33 — YoutubeFusionBlock : pour les projets type 'youtube', on saute Phase 3
// et on ajoute juste deux champs (titre + description YouTube) sous Phase 2.
function YoutubeFusionBlock({ task, allowEdit, onSaved }) {
  const [title, setTitle] = useState(task.publication_title || '');
  const [desc, setDesc] = useState(task.publication_description || '');
  const [saving, setSaving] = useState(null);
  const save = async (field, value) => {
    setSaving(field);
    try {
      await api.patch(`/tasks/${task.id}`, { [field]: value });
      toast.success('Enregistré');
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(null); }
  };
  return (
    <div data-testid="youtube-fusion-block" style={{
      padding: '14px 16px', borderRadius: 14,
      background: 'rgba(255,107,107,0.06)',
      border: '1.5px solid rgba(255,107,107,0.40)',
    }}>
      <div className="asa-eyebrow" style={{ marginBottom: 10, color: '#FF6B6B' }}>
        <Video size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
        Vidéo YouTube — Titre & Description
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 4 }}>Titre YouTube</div>
          <input
            data-testid="youtube-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => { if (title !== task.publication_title) save('publication_title', title); }}
            disabled={!allowEdit}
            placeholder="Ex: J'ai testé X pendant 30 jours et voilà ce qui s'est passé…"
            style={{
              width: '100%', padding: '8px 11px',
              background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 8,
              color: 'var(--t1)', fontFamily: 'Lexend', fontSize: 13,
            }}
          />
        </div>
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 4 }}>Description YouTube</div>
          <textarea
            data-testid="youtube-desc-input"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            onBlur={() => { if (desc !== task.publication_description) save('publication_description', desc); }}
            disabled={!allowEdit}
            rows={4}
            placeholder="Description complète à coller dans YouTube (timestamps, liens, hashtags…)"
            style={{
              width: '100%', padding: '8px 11px',
              background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 8,
              color: 'var(--t1)', fontFamily: 'Lexend', fontSize: 12.5, resize: 'vertical',
            }}
          />
        </div>
        {saving && (
          <div style={{ fontSize: 11, color: 'var(--t3)' }}>
            <Loader2 size={9} className="spin" /> Enregistrement…
          </div>
        )}
      </div>
    </div>
  );
}
